/* popup-overlay.js — newsletter popup (Athens `popup-overlay` section).
 *
 * rein-popup-rules-2026-09-28 (mobile ergonomics audit, the owner):
 * Clarity showed the popup absorbing every touch on tablets 8 s after load and
 * sitting over the installer locator. The stock class opened on a timer only,
 * closed only via its × button (the base class's outside-click test compares
 * nodeName against MODAL-DIALOG, which this element is not), and remembered a
 * close forever. Rules now:
 *
 *   TRIGGER    after 45 s OR once the visitor has scrolled 60 % of the page —
 *              whichever comes first. The section's delay setting (step 2, so
 *              it is stored as 44) can only lengthen the timer: the floor is
 *              45 s regardless of the customizer value.
 *   NEVER ON   /pages/find-an-installer, every /policies/* page, the shipping /
 *              returns / warranty / freight-quote pages, /cart, checkout, quiz
 *              pages, account pages.
 *   ADS        a visitor landing from an ad (utm_source / gclid / gbraid /
 *              wbraid / fbclid / msclkid / ttclid in the URL) does not get it on
 *              that first page view; later page views in the session may.
 *   CLOSE      × button (44 px), a tap anywhere on the backdrop, Escape.
 *              The page is never scroll-locked by this popup — not while it is
 *              open and not after it closes (body.overflow-hidden is removed on
 *              both show() and hide()).
 *   FREQUENCY  closed → not again for 7 days. Signed up → never again.
 *              The legacy `dismissed` value (stored by the old class) still
 *              counts as a permanent opt-out so nobody who already closed it
 *              sees it again.
 *   SUCCESS    after the form posts Shopify reloads the page with
 *              ?customer_posted=true; the popup then opens at once so the
 *              success message is seen, and the signed-up flag is stored.
 *
 * The section's `hide_on_mobile` setting (CSS .hidden-mobile) is respected:
 * when the element is display:none no timer or scroll listener is started.
 */
if (!customElements.get("popup-overlay")) {
  customElements.define(
    "popup-overlay",
    class PopupOverlay extends ModalDialog {
      constructor() {
        super();
        this.delay = Math.max(45, parseInt(this.dataset.delay, 10) || 0) * 1000;
        this.scrollShare = 0.6;
        this.id = this.getAttribute("id");
        this.moved = true;
        this.keyClosed = "rein-popup-closed-at";
        this.keySigned = "rein-popup-signed-up";
        this.keyAdLanding = "rein-popup-ad-landing";
        this.armed = false;
        this.timer = null;
        this.onScroll = this.onScroll.bind(this);
        this.onWrapClick = this.onWrapClick.bind(this);
        this.wrap = this.querySelector(".modal-dialog-wrap");
        this.dialog = this.querySelector(".modal-dialog");
        this.form = this.querySelector("form");
        if (this.wrap) this.wrap.addEventListener("click", this.onWrapClick);
        if (this.form) {
          this.form.addEventListener("submit", () => this.store(this.keySigned, "1"));
        }
      }

      /* ---------- storage helpers (private mode / blocked storage safe) */
      read(key) {
        try { return localStorage.getItem(key); } catch (e) { return null; }
      }
      store(key, value) {
        try { localStorage.setItem(key, value); } catch (e) { /* ignore */ }
      }
      sessionRead(key) {
        try { return sessionStorage.getItem(key); } catch (e) { return null; }
      }
      sessionStore(key, value) {
        try { sessionStorage.setItem(key, value); } catch (e) { /* ignore */ }
      }

      /* ---------- eligibility */
      suppressedPath() {
        const p = (location.pathname || "").toLowerCase();
        if (p === "/cart" || p.indexOf("/cart/") === 0) return true;
        if (p.indexOf("/checkouts") === 0 || p.indexOf("/checkout") === 0) return true;
        if (p.indexOf("/policies") === 0) return true;
        if (p.indexOf("/account") === 0 || p.indexOf("/customer") === 0) return true;
        if (p === "/pages/find-an-installer") return true;
        if (p.indexOf("/pages/") === 0) {
          const h = p.slice(7);
          if (/shipping|return|refund|warranty|freight|quiz|policy|terms|privacy/.test(h)) return true;
        }
        if (/quiz/.test(p)) return true;
        return false;
      }

      adLanding() {
        const q = location.search || "";
        return /[?&](utm_source|gclid|gbraid|wbraid|fbclid|msclkid|ttclid)=/i.test(q);
      }

      hiddenByCss() {
        return getComputedStyle(this).display === "none";
      }

      eligible() {
        if (Shopify.designMode) return false;
        if (this.hiddenByCss()) return false;
        if (this.read(this.keySigned) === "1") return false;
        if (this.read(this.id) === "dismissed") return false; // legacy permanent dismiss
        const closedAt = parseInt(this.read(this.keyClosed), 10);
        if (closedAt && Date.now() - closedAt < 7 * 24 * 60 * 60 * 1000) return false;
        if (this.suppressedPath()) return false;
        if (this.adLanding() && !this.sessionRead(this.keyAdLanding)) {
          // first page view from an ad: skip, but allow later views this session
          this.sessionStore(this.keyAdLanding, "1");
          return false;
        }
        return true;
      }

      connectedCallback() {
        const posted = /[?&]customer_posted=true/.test(location.search) && this.querySelector(".form-status-success");
        if (posted && !this.hiddenByCss()) {
          this.store(this.keySigned, "1");
          this.show();
        } else if (this.eligible()) {
          this.arm();
        }
        return super.connectedCallback();
      }

      arm() {
        if (this.armed) return;
        this.armed = true;
        this.timer = setTimeout(() => this.trigger(), this.delay);
        window.addEventListener("scroll", this.onScroll, { passive: true });
        this.onScroll();
      }

      disarm() {
        this.armed = false;
        if (this.timer) clearTimeout(this.timer);
        this.timer = null;
        window.removeEventListener("scroll", this.onScroll);
      }

      onScroll() {
        const de = document.documentElement;
        const max = de.scrollHeight - window.innerHeight;
        if (max <= 0) return;
        if (window.scrollY / max >= this.scrollShare) this.trigger();
      }

      trigger() {
        if (!this.armed) return;
        this.disarm();
        if (this.hasAttribute("open")) return;
        if (this.hiddenByCss()) return;
        this.show();
      }

      /* ---------- open / close */
      onWrapClick(event) {
        // A tap on the backdrop (outside the dialog box) closes the popup.
        if (this.dialog && !this.dialog.contains(event.target)) {
          event.preventDefault();
          this.hide();
        }
      }

      show() {
        super.show();
        document.body.classList.remove("overflow-hidden");
        document.dispatchEvent(new CustomEvent("popup-overlay:open", { detail: { id: this.id } }));
      }

      hide() {
        super.hide();
        document.body.classList.remove("overflow-hidden");
        this.disarm();
        document.dispatchEvent(new CustomEvent("popup-overlay:close", { detail: { id: this.id } }));
        if (!Shopify.designMode) {
          this.store(this.keyClosed, String(Date.now()));
        }
      }
    },
  );
}
